/**
 * Recherche globale de la barre superieure.
 *
 * Elle interroge la liste des leads existante (`GET /api/v1/leads?q=`) : pas
 * de route dediee, les regles de visibilite de l'API s'appliquent donc telles
 * quelles. Motif ARIA de la liste deroulante editable (combobox + listbox) :
 * le focus reste dans le champ, l'option active est annoncee par
 * `aria-activedescendant`.
 */
import { useEffect, useId, useRef, useState } from 'react';
import type { KeyboardEvent as EvenementClavierReact } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import { useLeads } from '../lib/requetes';
import { useValeurDifferee } from '../lib/hooks';
import { pluriel } from '../lib/format';
import { Kbd, Rouet, coucheModaleOuverte } from './ui';
import { MarqueurOpposition } from './lead';

export const DELAI_RECHERCHE_MS = 250;
export const NB_RESULTATS_MAX = 8;
/** En dessous, la recherche renverrait presque toute la base. */
const LONGUEUR_MIN = 2;

/** Un raccourci ne doit jamais voler une frappe destinee a un champ. */
function estZoneDeSaisie(cible: EventTarget | null): boolean {
  if (!(cible instanceof HTMLElement)) return false;
  return cible.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(cible.tagName);
}

export function RechercheGlobale() {
  const naviguer = useNavigate();
  const idListe = useId();
  const refChamp = useRef<HTMLInputElement>(null);
  const [saisie, setSaisie] = useState('');
  const [ouverte, setOuverte] = useState(false);
  const [indexActif, setIndexActif] = useState(0);

  const terme = useValeurDifferee(saisie.trim(), DELAI_RECHERCHE_MS);
  const actif = terme.length >= LONGUEUR_MIN;
  const requete = useLeads(
    {
      q: terme,
      page: 1,
      taillePage: NB_RESULTATS_MAX,
      tri: 'denomination',
      ordre: 'asc',
      // Retrouver une fiche par son nom doit marcher meme en opposition ; le marqueur le signale.
      inclureNePlusContacter: true,
    },
    { actif },
  );
  const resultats = actif ? (requete.data?.donnees ?? []).slice(0, NB_RESULTATS_MAX) : [];
  const listeVisible = ouverte && actif;
  const avecResultats = listeVisible && resultats.length > 0;
  // Resultats du terme precedent, gardes a l'ecran le temps de la recherche
  // (pas de clignotement) : Entree ou un clic ouvrirait une fiche qui ne
  // correspond plus a la saisie. Ils le sont des la frappe, pas seulement
  // une fois la requete partie : l'anti-rebond laisse 250 ms entre les deux.
  const perimes = actif && (requete.isPlaceholderData || saisie.trim() !== terme);
  const selectionnable = avecResultats && !perimes;
  const enCours = requete.isPending || perimes;

  const annonce = !listeVisible
    ? ''
    : requete.isError
      ? 'La recherche a échoué. Réessayez dans un instant.'
      : enCours
        ? 'Recherche…'
        : resultats.length === 0
          ? `Aucun lead ne correspond à « ${terme} ».`
          : `${pluriel(resultats.length, 'lead trouvé', 'leads trouvés')}.`;

  // Chaque nouvelle liste replace la selection sur le premier resultat : Entree ouvre le plus probable.
  useEffect(() => {
    setIndexActif(0);
  }, [terme, requete.data]);

  useEffect(() => {
    const surTouche = (evenement: KeyboardEvent): void => {
      if (coucheModaleOuverte()) return;
      const raccourciK = (evenement.ctrlKey || evenement.metaKey) && evenement.key.toLowerCase() === 'k';
      const raccourciBarre =
        evenement.key === '/' &&
        !evenement.ctrlKey &&
        !evenement.metaKey &&
        !evenement.altKey &&
        !estZoneDeSaisie(evenement.target);
      if (!raccourciK && !raccourciBarre) return;
      evenement.preventDefault();
      refChamp.current?.focus();
      refChamp.current?.select();
    };
    document.addEventListener('keydown', surTouche);
    return () => document.removeEventListener('keydown', surTouche);
  }, []);

  const choisir = (id: string): void => {
    setSaisie('');
    setOuverte(false);
    refChamp.current?.blur();
    naviguer(`/leads/${id}`);
  };

  const surTouche = (evenement: EvenementClavierReact<HTMLInputElement>): void => {
    if (evenement.key === 'ArrowDown' || evenement.key === 'ArrowUp') {
      evenement.preventDefault();
      if (!listeVisible) {
        setOuverte(true);
        return;
      }
      if (!selectionnable) return;
      const pas = evenement.key === 'ArrowDown' ? 1 : -1;
      setIndexActif((index) => (index + pas + resultats.length) % resultats.length);
    } else if (evenement.key === 'Enter') {
      const cible = selectionnable ? resultats[indexActif] : undefined;
      if (!cible) return;
      evenement.preventDefault();
      choisir(cible.id);
    } else if (evenement.key === 'Escape') {
      evenement.preventDefault();
      if (listeVisible) {
        setOuverte(false);
        return;
      }
      setSaisie('');
      refChamp.current?.blur();
    }
  };

  const idOption = (index: number): string => `${idListe}-option-${index}`;

  return (
    <div
      className="relative"
      onBlur={(evenement) => {
        if (!evenement.currentTarget.contains(evenement.relatedTarget)) setOuverte(false);
      }}
    >
      <Search
        aria-hidden="true"
        className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-encre-tenue"
      />
      <input
        ref={refChamp}
        type="text"
        role="combobox"
        aria-label="Recherche rapide"
        aria-haspopup="listbox"
        aria-expanded={avecResultats}
        aria-controls={avecResultats ? idListe : undefined}
        aria-autocomplete="list"
        aria-activedescendant={selectionnable ? idOption(indexActif) : undefined}
        autoComplete="off"
        spellCheck={false}
        placeholder="Rechercher un lead…"
        className="champ pr-10 pl-9"
        value={saisie}
        onChange={(evenement) => {
          setSaisie(evenement.target.value);
          setOuverte(true);
        }}
        onFocus={() => setOuverte(true)}
        onKeyDown={surTouche}
      />
      {saisie === '' && (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute top-1/2 right-2.5 hidden -translate-y-1/2 sm:block"
        >
          <Kbd>/</Kbd>
        </span>
      )}
      {/* Retenir le mousedown garde le focus dans le champ : sans cela, cliquer une option fermerait la liste avant que le clic soit traite. */}
      {listeVisible && (
        <div
          className="surface-menu absolute inset-x-0 top-full z-40 mt-1.5 overflow-hidden"
          onMouseDown={(evenement) => evenement.preventDefault()}
        >
          {avecResultats ? (
            <ul
              id={idListe}
              role="listbox"
              aria-label="Résultats de la recherche"
              className="max-h-80 overflow-y-auto p-1"
            >
              {resultats.map((lead, index) => (
                <li
                  key={lead.id}
                  id={idOption(index)}
                  role="option"
                  aria-selected={selectionnable && index === indexActif}
                  aria-disabled={perimes || undefined}
                  onMouseEnter={() => {
                    if (selectionnable) setIndexActif(index);
                  }}
                  onClick={() => {
                    if (selectionnable) choisir(lead.id);
                  }}
                  className={`flex items-center justify-between gap-3 rounded-md px-2.5 py-2 ${
                    perimes ? 'opacity-60' : 'cursor-pointer'
                  } ${selectionnable && index === indexActif ? 'bg-accent-50' : ''}`}
                >
                  <span className="min-w-0">
                    <span className="block truncate text-base font-medium text-encre">{lead.denomination}</span>
                    <span className="block truncate text-sm text-encre-discrete">
                      {[lead.codePostal, lead.ville].filter(Boolean).join(' ') || 'Commune inconnue'}
                    </span>
                  </span>
                  {lead.nePlusContacter && <MarqueurOpposition compact />}
                </li>
              ))}
            </ul>
          ) : (
            <p className="flex items-center gap-2 px-3 py-2.5 text-sm text-encre-discrete">
              {requete.isError ? (
                'La recherche a échoué. Réessayez dans un instant.'
              ) : enCours ? (
                <>
                  <Rouet /> Recherche…
                </>
              ) : (
                `Aucun lead ne correspond à « ${terme} ».`
              )}
            </p>
          )}
        </div>
      )}
      {/* Une seule region d'annonce, presente des le montage : un lecteur
          d'ecran n'annonce que ce qui change dans une region deja la. */}
      <p role="status" className="sr-only">
        {annonce}
      </p>
    </div>
  );
}
